import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, X, Loader2, Sparkles, Image as ImageIcon, ArrowRight, Bookmark, Lock } from "lucide-react";
import { api } from "../lib/api";
import { useProfile } from "../context/ProfileContext";
import { useAuth } from "../context/AuthContext";
import { ProductCard } from "./ProductCard";
import { toast } from "sonner";

const _fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const dataUrl = reader.result || "";
        const [, b64] = String(dataUrl).split(",", 2);
        resolve(b64 || "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

/**
 * "Shop this look" — snap-or-upload a clothing photo, Gemini describes it,
 * the backend matches it against our 8-store catalog (filtered to the user's
 * size when a profile exists). Renders inside a brutalist full-screen modal.
 */
export const VisualSearchModal = ({ onClose }) => {
    const fileRef = useRef(null);
    const { profileId } = useProfile();
    const { user } = useAuth();
    const [preview, setPreview] = useState(null);
    const [pickedFile, setPickedFile] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);

    const onPick = async (file) => {
        if (!file) return;
        setError(null);
        setResult(null);
        setSaved(false);
        if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
            setError("Use JPEG, PNG or WEBP.");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            setError("Image is over 8 MB — pick a smaller one.");
            return;
        }
        setPreview(URL.createObjectURL(file));
        setPickedFile(file);
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append("image", file, file.name || "garment.jpg");
            const url = profileId ? `/visual-search?profile_id=${profileId}` : "/visual-search";
            const { data } = await api.post(url, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            if ((data.products || []).length === 0) {
                toast("No exact matches found", { description: "Try a clearer, well-lit photo." });
            }
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || "Visual search failed.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const saveThisLook = async () => {
        if (!pickedFile || !result?.description) return;
        setSaving(true);
        try {
            const b64 = await _fileToBase64(pickedFile);
            const top_match_ids = (result.products || []).slice(0, 5).map((p) => p.id);
            await api.post("/saved-looks", {
                description: result.description,
                image_b64: b64,
                mime: pickedFile.type || "image/jpeg",
                top_match_ids,
            });
            setSaved(true);
            toast.success("Saved to your looks");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Couldn't save look.";
            toast.error(msg);
        } finally { setSaving(false); }
    };

    return (
        <div
            data-testid="visual-search-modal"
            className="fixed inset-0 z-[60] bg-black/80 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="max-w-5xl mx-auto mt-6 mb-12 bg-[#FDFBF7] border-4 border-black shadow-brutal-lg"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b-4 border-black bg-[#CCFF00] px-5 py-3">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-widest">/ AI visual search /</p>
                        <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight inline-flex items-center gap-2">
                            <Camera className="w-6 h-6" /> Shop this look
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="visual-search-close"
                        className="border-2 border-black bg-white p-1.5 shadow-brutal-sm"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="grid md:grid-cols-2 gap-5 p-5">
                    {/* Left — upload + preview */}
                    <div className="space-y-3">
                        <p className="font-mono text-xs leading-relaxed text-black/70">
                            Snap a photo of a clothing item you saw — on a friend, in a store, on TikTok — and we&apos;ll
                            find the closest matches from our 8 partner retailers in <strong>your size</strong>.
                        </p>
                        <div className="border-2 border-dashed border-black bg-white aspect-[3/4] flex flex-col items-center justify-center relative overflow-hidden">
                            {preview ? (
                                <img src={preview} alt="Your photo" className="w-full h-full object-cover" />
                            ) : (
                                <>
                                    <ImageIcon className="w-10 h-10 mb-2 text-black/40" />
                                    <p className="font-mono text-xs uppercase tracking-widest text-black/60">
                                        Drop or snap a garment
                                    </p>
                                </>
                            )}
                            {loading && (
                                <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-[#CCFF00] gap-2" data-testid="visual-search-loading">
                                    <Loader2 className="w-8 h-8 animate-spin" />
                                    <p className="font-mono text-xs uppercase tracking-widest">Analyzing…</p>
                                </div>
                            )}
                        </div>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => onPick(e.target.files?.[0])}
                            data-testid="visual-search-input"
                        />
                        <button
                            onClick={() => fileRef.current?.click()}
                            disabled={loading}
                            data-testid="visual-search-pick"
                            className="w-full bg-black text-[#CCFF00] border-2 border-black px-4 py-2.5 font-display font-black uppercase tracking-widest shadow-brutal-sm btn-press disabled:opacity-60 inline-flex items-center justify-center gap-2"
                        >
                            <Camera className="w-4 h-4" /> {preview ? "Try another photo" : "Snap or upload"}
                        </button>
                        {error && (
                            <p data-testid="visual-search-error" className="border-2 border-[#FF00FF] bg-white text-[#FF00FF] px-3 py-2 font-mono text-xs uppercase tracking-widest">
                                {error}
                            </p>
                        )}
                    </div>

                    {/* Right — AI description + matches */}
                    <div className="space-y-3">
                        {result?.description && (
                            <div data-testid="visual-search-description" className="border-2 border-black bg-[#CCFF00] p-3 font-mono text-xs space-y-1.5">
                                <p className="font-bold uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
                                    <Sparkles className="w-3 h-3" /> Gemini sees:
                                </p>
                                {result.description.category && (
                                    <p><strong>Category:</strong> {result.description.category}</p>
                                )}
                                {result.description.primary_color && (
                                    <p><strong>Colour:</strong> {result.description.primary_color}{result.description.secondary_color ? ` / ${result.description.secondary_color}` : ""}</p>
                                )}
                                {result.description.cut && (
                                    <p><strong>Cut:</strong> {result.description.cut}</p>
                                )}
                                {!!(result.description.style_tags || []).length && (
                                    <p><strong>Tags:</strong> {result.description.style_tags.join(", ")}</p>
                                )}
                                {result.description.vibe && (
                                    <p className="italic">&ldquo;{result.description.vibe}&rdquo;</p>
                                )}
                            </div>
                        )}

                        {result && (
                            <div className="border-2 border-black bg-black text-[#CCFF00] px-3 py-2 font-mono text-[10px] uppercase tracking-widest">
                                {result.total ?? 0} matches across {result.stores_count ?? 0} stores
                                {result.fallback === "category_only" && <span className="text-[#FF00FF]"> · broadened to category</span>}
                                {profileId && <span> · filtered to your fit</span>}
                            </div>
                        )}

                        {result?.products?.length > 0 ? (
                            <div className="grid grid-cols-2 gap-3 max-h-[480px] overflow-y-auto pr-1" data-testid="visual-search-results">
                                {result.products.slice(0, 8).map((p) => (
                                    <ProductCard key={p.id} product={p} />
                                ))}
                            </div>
                        ) : result ? (
                            <div className="border-4 border-dashed border-black p-6 text-center font-mono text-xs uppercase tracking-widest text-black/60">
                                No catalog hits. Try a clearer photo or a different garment.
                            </div>
                        ) : (
                            <div className="border-4 border-dashed border-black p-6 text-center font-mono text-xs uppercase tracking-widest text-black/40">
                                Results appear here once you snap a photo.
                            </div>
                        )}

                        {result?.products?.length > 0 && (
                            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                                <Link
                                    to="/search"
                                    onClick={onClose}
                                    data-testid="visual-search-see-all"
                                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest underline"
                                >
                                    See full search results <ArrowRight className="w-3 h-3" />
                                </Link>
                                {user ? (
                                    <button
                                        onClick={saveThisLook}
                                        disabled={saving || saved}
                                        data-testid="visual-search-save-look"
                                        className={`inline-flex items-center gap-1.5 border-2 border-black px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest shadow-brutal-sm btn-press ${saved ? "bg-[#CCFF00] text-black" : "bg-[#FF00FF] text-white"}`}
                                    >
                                        <Bookmark className={`w-3.5 h-3.5 ${saved ? "fill-current" : ""}`} />
                                        {saving ? "Saving…" : saved ? "Saved to looks ✓" : "Save this look"}
                                    </button>
                                ) : (
                                    <Link
                                        to="/login?next=/wishlist"
                                        onClick={onClose}
                                        data-testid="visual-search-signin-to-save"
                                        className="inline-flex items-center gap-1.5 border-2 border-black bg-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest shadow-brutal-sm btn-press"
                                    >
                                        <Lock className="w-3.5 h-3.5" /> Sign in to save
                                    </Link>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
